/**
 * Loan Performance Dashboard (Kinerja Pinjaman)
 */

import m from "mithril";
import { getMithril } from '../utils.js';

export default {
    oninit(vnode) {
        const today = new Date().toISOString().slice(0, 10);
        vnode.state.startDate = today;
        vnode.state.endDate = today;
        vnode.state.performanceData = [];
        vnode.state.isLoading = false;
        vnode.state.error = null;

        this.fetchPerformanceData(vnode);
    },

    async fetchPerformanceData(vnode) {
        vnode.state.isLoading = true;
        vnode.state.error = null;
        try {
            const data = await getMithril(`/api/loans-performance?startDate=${vnode.state.startDate}&endDate=${vnode.state.endDate}`);
            if (data && data.success) {
                // The backend returns an object with detailedStats array
                vnode.state.performanceData = data.data.detailedStats || [];
                vnode.state.summary = {
                    previousTotalAmount: data.data.previousTotalAmount,
                    previousTotalCount: data.data.previousTotalCount,
                    todayTotalAmount: data.data.todayTotalAmount,
                    todayTotalCount: data.data.todayTotalCount
                };
            } else {
                vnode.state.error = data?.error || 'Failed to load performance data';
            }
        } catch (err) {
            vnode.state.error = err.message || 'System error loading data';
        } finally {
            vnode.state.isLoading = false;
            m.redraw();
        }
    },

    formatIDR(value) {
        return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(value || 0);
    },

    view(vnode) {
        const totalDisbursementCount = vnode.state.summary?.todayTotalCount || 0;
        const totalDisbursementAmount = vnode.state.summary?.todayTotalAmount || 0;
        const isRange = vnode.state.startDate !== vnode.state.endDate;
        const periodLabel = isRange ? "Periode" : "Harian";

        return m("div.p-6.max-w-7xl.mx-auto", [
            // Header Section
            m("div.flex.flex-col.lg:flex-row.justify-between.items-start.lg:items-center.mb-8", [
                m("div", [
                    m("h1.text-2xl.font-bold.text-gray-900.dark:text-white", "Kinerja Pinjaman"),
                    m("p.text-gray-600.dark:text-gray-400.mt-1", `Agregasi pencairan pinjaman ${periodLabel.toLowerCase()} berdasar petugas`)
                ]),
                m("div.mt-4.lg:mt-0.flex.flex-wrap.items-center.gap-4", [
                    m("div.flex.items-center.gap-2", [
                        m("div.relative", [
                            m("input[type=date].pl-10.pr-4.py-2.border.border-gray-300.dark:border-gray-600.rounded-lg.bg-white.dark:bg-gray-800.text-gray-900.dark:text-white.focus:ring-2.focus:ring-orange-500", {
                                value: vnode.state.startDate,
                                onchange: (e) => {
                                    vnode.state.startDate = e.target.value;
                                    this.fetchPerformanceData(vnode);
                                }
                            }),
                            m("i.ri-calendar-line.absolute.left-3.top-2_5.text-gray-400")
                        ]),
                        m("span.text-gray-500.dark:text-gray-400", "-"),
                        m("div.relative", [
                            m("input[type=date].pl-10.pr-4.py-2.border.border-gray-300.dark:border-gray-600.rounded-lg.bg-white.dark:bg-gray-800.text-gray-900.dark:text-white.focus:ring-2.focus:ring-orange-500", {
                                value: vnode.state.endDate,
                                onchange: (e) => {
                                    vnode.state.endDate = e.target.value;
                                    this.fetchPerformanceData(vnode);
                                }
                            }),
                            m("i.ri-calendar-line.absolute.left-3.top-2_5.text-gray-400")
                        ]),
                    ]),
                    m("button.px-4.py-2.bg-orange-600.text-white.rounded-lg.hover:bg-orange-700.transition-colors.shadow-sm.flex.items-center", {
                        onclick: () => this.fetchPerformanceData(vnode)
                    }, [
                        m("i.ri-refresh-line.mr-2"),
                        "Refresh"
                    ])
                ])
            ]),

            // Stats Overview
            m("div.grid.grid-cols-1.md:grid-cols-2.gap-6.mb-8", [
                m("div.bg-white.dark:bg-gray-800.p-6.rounded-xl.shadow-sm.border.border-gray-100.dark:border-gray-700.flex.items-center", [
                    m("div.w-12.h-12.rounded-full.bg-orange-100.dark:bg-orange-900/50.flex.items-center.justify-center.mr-4", [
                        m("i.ri-exchange-dollar-line.text-2xl.text-orange-600.dark:text-orange-400")
                    ]),
                    m("div", [
                        m("p.text-sm.text-gray-500.dark:text-gray-400", `Total Pencairan ${periodLabel}`),
                        m("h3.text-2xl.font-black.text-gray-900.dark:text-white", this.formatIDR(totalDisbursementAmount))
                    ])
                ]),
                m("div.bg-white.dark:bg-gray-800.p-6.rounded-xl.shadow-sm.border.border-gray-100.dark:border-gray-700.flex.items-center", [
                    m("div.w-12.h-12.rounded-full.bg-blue-100.dark:bg-blue-900/50.flex.items-center.justify-center.mr-4", [
                        m("i.ri-user-follow-line.text-2xl.text-blue-600.dark:text-blue-400")
                    ]),
                    m("div", [
                        m("p.text-sm.text-gray-500.dark:text-gray-400", `Total Nasabah ${periodLabel}`),
                        m("h3.text-2xl.font-black.text-gray-900.dark:text-white", `${totalDisbursementCount} Orang`)
                    ])
                ])
            ]),

            vnode.state.error && m("div.p-4.mb-6.bg-red-50.dark:bg-red-900/30.text-red-600.dark:text-red-400.rounded-lg.border.border-red-200.dark:border-red-800.flex.items-center", [
                m("i.ri-error-warning-line.mr-2.text-lg"),
                vnode.state.error
            ]),

            vnode.state.isLoading ? m("div.py-12.flex.flex-col.items-center.justify-center", [
                m("div.animate-spin.rounded-full.h-12.w-12.border-b-2.border-orange-600.mb-4"),
                m("p.text-gray-500.dark:text-gray-400", "Mengambil data rekap...")
            ]) :
            m("div.bg-white.dark:bg-gray-800.rounded-xl.shadow-sm.border.border-gray-200.dark:border-gray-700.overflow-hidden", [
                m("table.w-full.text-left", [
                    m("thead.bg-gray-50.dark:bg-gray-750.text-gray-600.dark:text-gray-400.text-xs.uppercase", [
                        m("tr", [
                            m("th.px-6.py-3", "Petugas"),
                            m("th.px-6.py-3", "Peran"),
                            m("th.px-6.py-3.text-center", "Jumlah Nasabah"),
                            m("th.px-6.py-3.text-right", "Total Pencairan")
                        ])
                    ]),
                    m("tbody.divide-y.divide-gray-100.dark:divide-gray-700", 
                        vnode.state.performanceData.length === 0 ? 
                        m("tr", m("td.px-6.py-10.text-center.text-gray-500[colspan=4]", "Tidak ada data untuk periode ini.")) :
                        vnode.state.performanceData.map(item => m("tr.hover:bg-gray-50.dark:hover:bg-gray-750", [
                            m("td.px-6.py-4.text-sm.font-medium.text-gray-900.dark:text-white", item.name),
                            m("td.px-6.py-4.text-sm.text-gray-500.capitalize", item.role),
                            m("td.px-6.py-4.text-sm.text-center.text-gray-700.dark:text-gray-300", item.transactionCount),
                            m("td.px-6.py-4.text-sm.text-right.font-bold.text-gray-900.dark:text-white", this.formatIDR(item.totalAmount))
                        ]))
                    )
                ])
            ])
        ]);
    }

};
